import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import OAuthInput from '../OAuthFlow/shared/OAuthInput';
import OAuthButton from '../OAuthFlow/shared/OAuthButton';
import PolicyModal from './PolicyModal';

const Heading = styled.h1`
  font-size: 24px;
  font-weight: 500;
  color: ${p => p.theme.text};
  text-align: center;
  margin-bottom: 4px;
  line-height: 32px;
`;

const Subtext = styled.p`
  font-size: 15px;
  font-weight: 400;
  color: ${p => p.theme.textSecondary};
  text-align: center;
  line-height: 24px;
  margin-bottom: 24px;
`;

const TermsRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 4px 0 20px 0;
  font-size: 13px;
  line-height: 20px;
  color: ${p => p.theme.textSecondary};
  cursor: pointer;
  user-select: none;
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  margin-top: 2px;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  accent-color: ${p => p.theme.accent};
  cursor: pointer;
`;

const PolicyLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: ${p => p.theme.accent};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover { opacity: 0.85; }
`;

const TermsError = styled.div`
  font-size: 12px;
  color: ${p => p.theme.error};
  margin: -12px 0 16px 0;
`;

const CancelRow = styled.div`
  text-align: center;
  margin-top: 16px;
  font-size: 13px;
  color: ${p => p.theme.textTertiary};
  line-height: 18px;
`;

const CancelLink = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.textTertiary};
  font-family: ${p => p.theme.fontFamily};
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;

  &:hover { color: ${p => p.theme.textSecondary}; }
`;

export default function ConfirmStep({ claimToken, name, setName, email, setEmail, headers, onOtpSent, onCancel }) {
  const [loading, setLoading]                       = useState(false);
  const [error, setError]                           = useState(null);
  const [termsAccepted, setTermsAccepted]           = useState(false);
  const [showTermsRequired, setShowTermsRequired]   = useState(false);
  const [openPolicy, setOpenPolicy]                 = useState(null); // 'terms' | 'privacy' | null

  const closePolicy = useCallback(function() { setOpenPolicy(null); }, []);

  const handleSubmit = useCallback(async function(e) {
    if (e) e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedName  = name.trim();

    if (!trimmedEmail || !trimmedEmail.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!termsAccepted) {
      setShowTermsRequired(true);
      return;
    }

    setLoading(true);
    setError(null);
    setShowTermsRequired(false);

    try {
      const response = await fetch('/oauth/sso/claim/' + claimToken + '/send_otp', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ email: trimmedEmail, name: trimmedName, terms_accepted: true }),
      });
      const data = await response.json();

      if (response.ok) {
        setEmail(trimmedEmail);
        setName(trimmedName);
        onOtpSent(data);
      } else {
        setError(data.message || 'Could not send verification code.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [claimToken, email, name, termsAccepted, headers, onOtpSent, setEmail, setName]);

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Heading>Confirm your details</Heading>
        <Subtext>We&apos;ll send a verification code to your email to finish setting up your account.</Subtext>

        <OAuthInput
          label="Name"
          type="text"
          placeholder="Your name"
          value={name}
          onChange={function(e) { setName(e.target.value); setError(null); }}
          autoComplete="name"
        />

        <OAuthInput
          label="Email address"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={function(e) { setEmail(e.target.value); setError(null); }}
          error={error}
          autoComplete="email"
        />

        <TermsRow>
          <Checkbox
            checked={termsAccepted}
            onChange={function(e) {
              setTermsAccepted(e.target.checked);
              if (e.target.checked) setShowTermsRequired(false);
            }}
          />
          <span>
            I agree to the{' '}
            <PolicyLink type="button" onClick={function() { setOpenPolicy('terms'); }}>
              Terms of Service
            </PolicyLink>
            {' '}and{' '}
            <PolicyLink type="button" onClick={function() { setOpenPolicy('privacy'); }}>
              Privacy Policy
            </PolicyLink>
            .
          </span>
        </TermsRow>

        {showTermsRequired && !termsAccepted ? (
          <TermsError>Please agree to the Terms of Service and Privacy Policy to continue.</TermsError>
        ) : null}

        <OAuthButton type="submit" loading={loading} disabled={!email.trim() || !termsAccepted}>
          Continue
        </OAuthButton>

        <CancelRow>
          <CancelLink type="button" onClick={onCancel}>Cancel</CancelLink>
        </CancelRow>
      </form>

      {openPolicy ? <PolicyModal kind={openPolicy} onClose={closePolicy} /> : null}
    </>
  );
}
